import './polyfills';

import { IPortalShellInitializeOptions } from './models/globalScript';
import * as TestApi from './testApi';
import AccountAndTenants, { accountAndTenantsStore } from './utils/accountAndTenants/accountAndTenants';
import { AccountAndTenantsInstanceResolver } from './utils/accountAndTenants/accountAndTenantsInstanceResolver';
import { AppInsightsClient } from './utils/appInsights/appInsights';
import { AppInsightsInstanceResolver } from './utils/appInsights/appInsightsInstanceResolver';
import { EventHandlersInstanceResolver } from './utils/events/eventHandlersInstanceResolver';
import GlobalEventEmitter from './utils/events/globalEventEmitters';
import FeatureFlags, { featureFlagStore } from './utils/featureFlags/featureFlags';
import { GlobalPropsInstanceResolver } from './utils/globalProps/globalPropsInstanceResolver';
import ConsumerLogger from './utils/logger/consumerLogger';
import Logger from './utils/logger/logger';
import Pendo from './utils/pendo/pendo';
import Session from './utils/session/session';
import { sessionStore } from './utils/session/sessionStore';
import { getSignalRServiceInstance } from './utils/signalR/notificationSignalRHelper';

// A set of global APIs that are meant to be consumed for tests (ie, not for consumers)
window['__unsafe__PORTAL_SHELL_TEST_UTILS__'] = TestApi;

// Instantiate singleton services
Logger.setAppInsights(AppInsightsInstanceResolver.InstantiateShell(new AppInsightsClient()));

const pendo = new Pendo();
const featureFlags = new FeatureFlags(featureFlagStore);
const session = new Session(sessionStore);
const signalRService = getSignalRServiceInstance();
const accountAndTenants = AccountAndTenantsInstanceResolver.Instantiate(new AccountAndTenants(accountAndTenantsStore));

// Set up initialize function
const initialize = (options: IPortalShellInitializeOptions = { devProperties: { mode: 'auto' } }) => {
    Logger.debug('globalScript', 'initialize', { object: options });

    // Set up event handlers
    EventHandlersInstanceResolver.Instantiate({
        onTenantsNotAvailable: options.onTenantsNotAvailable,
        onTenantChanged: options.onTenantChanged,
        onTenantLoaded: options.onTenantLoaded,
        onThemeChanged: options.onThemeChanged,
        onThemeSelectChanged: options.onThemeSelectChanged,
        onThemeLoaded: options.onThemeLoaded,
        onLanguageChanged: options.onLanguageChanged,
        onLanguageLoaded: options.onLanguageLoaded,
        onPendoInitialized: options.onPendoInitialized,
        onLogout: options.onLogout,
    });

    // Set up global properties
    GlobalPropsInstanceResolver.Instantiate({
        devProperties: {
            ...options.devProperties,
            mode: options.devProperties?.mode ?? 'auto',
        },
        featureFlagOverrides: { ...options.featureFlagOverrides },
    });

    // Add the consumer app insights so that they can log using the telemetry-client-web library without implementing it themselves
    if (options.appInsightsConfig) {
        const platformInfo = AppInsightsInstanceResolver.ShellInstance?.getPlatformInfo?.();

        ConsumerLogger.setAppInsights(AppInsightsInstanceResolver.InstantiateConsumer(
            new AppInsightsClient(options.appInsightsConfig),
        ));

        if (options.appInsightsConfig.platformInfo) {
            AppInsightsInstanceResolver.ConsumerInstance?.setPlatformInfo?.(options.appInsightsConfig.platformInfo);
        }

        // If we have a platform info set on the shell instance, set it on the consumer instance
        if (platformInfo) {
            AppInsightsInstanceResolver.ConsumerInstance?.setPlatformInfo?.(platformInfo);
        }

        // If the consumer wants events from portal shell in their own app insights instance
        if (options.appInsightsConfig.logShellEvents) {
            Logger.logToConsumer = true;
        }

        window.PortalShell.Logger = ConsumerLogger;
    }
};

window.PortalShell = {
    initialize,
    Pendo: pendo,
    FeatureFlags: featureFlags,
    Session: session,
    SignalRService: signalRService,
    AccountAndTenants: accountAndTenants,
    EventHandlers: {},
    GlobalProperties: {
        devProperties: { mode: 'auto' },
        featureFlagOverrides: {},
    },
};

GlobalEventEmitter.emitPortalShellLoaded();
